import { css, cx } from "@emotion/css";
import { borderWidth, borderRadius, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import type { PopoverBasicHelpProps } from "../../Popover";
import { FormDescription } from "../Primitives/FormDescription";
import { InputError } from "../Primitives/InputError";
import { InputLabel } from "../Primitives/InputLabel";
import { inputDisabledStyles, inputErrorStyles, inputReadOnlyStyles, inputStyles } from "../Primitives/SharedFormStyling.styles";
import type { DescriptionContent } from "../utils/descriptionWithLinks";
export interface TextareaProps {
    /**
     * The label for the textarea field
     */
    label: string;
    /**
     * The current value of the textarea
     */
    value?: string;
    /**
     * The placeholder text to display when textarea is empty
     */
    placeholder?: string;
    /**
     * The description text to display below the textarea
     */
    description?: string | DescriptionContent;
    /**
     * Error message to display
     */
    error?: string;
    /**
     * Whether the field is disabled
     */
    disabled?: boolean;
    /**
     * Whether the field is required
     */
    hasRequiredMarker?: boolean;
    /**
     * Whether to show optional marker
     */
    hasOptionalMarker?: boolean;
    /**
     * Uses a monospace font for textarea
     */
    monospace?: boolean;
    /**
     * PopoverBasicHelp component to display additional help information
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
    /**
     * Whether to autofocus the textarea
     */
    autoFocus?: boolean;
    /**
     * Whether the textarea is readonly
     */
    readOnly?: boolean;
    /**
     * The name attribute for the textarea
     */
    name: string;
    /**
     * The number of visible text lines for the textarea (Default is 4)
     */
    rows?: number;
    /**
     * The maximum number of characters allowed
     */
    maxLength?: number;
    /**
     * The minimum number of characters required
     */
    minLength?: number;
    /**
     * Whether the textarea should automatically resize
     */
    autoResize?: boolean;
    /**
     * The action to perform when the form control field is changed.
     */
    onChange: (newValue: string) => void;
}
/** Textarea component  used for multi-line text input*/
/**
 * @param props - TextareaProps
 * @param props.label - The label for the textarea field
 * @param props.value - The current value of the textarea
 * @param props.placeholder - The placeholder text to display when textarea is empty
 * @param props.description - The description text to display below the textarea
 * @param props.error - Error message to display
 * @param props.disabled - Whether the field is disabled
 * @param props.hasRequiredMarker - Whether the field is required
 * @param props.hasOptionalMarker - Whether to show optional marker
 * @param props.monospace - Whether to use a monospace font for the textarea
 * @param props.popover - PopoverBasicHelp component to display additional help information
 * @param props.autoFocus - Whether to autofocus the textarea
 * @param props.readOnly - Whether the textarea is readonly
 * @param props.name - The name attribute for the textarea
 * @param props.rows - The number of visible text lines for the textarea (Default is 4)
 * @param props.maxLength - The maximum number of characters allowed
 * @param props.minLength - The minimum number of characters required
 * @param props.autoResize - Whether the textarea should automatically resize
 * @param props.onChange - The action to perform when the form control field is changed.
 */
export function Textarea({ label, value, placeholder, description, error, disabled = false, hasRequiredMarker = false, hasOptionalMarker = false, monospace = false, popover, autoFocus = false, readOnly = false, name, rows = 4, maxLength, minLength, autoResize = false, onChange, }: TextareaProps) {
    const handleChange = React.useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (onChange) {
            onChange(event.target.value);
        }
    }, [onChange]);
    const baseId = React.useId();
    const textareaId = `textarea-${name}-${baseId}`;
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    // Auto-resize functionality
    // NOTE: 29/10/25 This Javascript can eventually be replaced by field-sizing:content
    // when it's supported in Firefox and fully in Safari
    React.useLayoutEffect(() => {
        if (!autoResize || !textareaRef.current)
            return;
        const textarea = textareaRef.current;
        const adjustHeight = () => {
            textarea.style.height = "auto";
            textarea.style.height = `${textarea.scrollHeight}px`;
        };
        adjustHeight();
        textarea.addEventListener("input", adjustHeight);
        return () => textarea.removeEventListener("input", adjustHeight);
    }, [autoResize, value]);
    return (<div className={containerStyles}>
            <div>
                <InputLabel label={label} htmlFor={textareaId} isDisabled={disabled} hasRequiredMarker={hasRequiredMarker} hasOptionalMarker={hasOptionalMarker} popover={popover}/>
                {description && <FormDescription id={`${textareaId}-description`} description={description} isDisabled={disabled}/>}
            </div>
            <div className={cx(textareaContainerStyles)}>
                <textarea ref={textareaRef} id={textareaId} name={name} value={value} placeholder={placeholder} disabled={disabled} readOnly={readOnly} aria-readonly={readOnly} aria-required={hasRequiredMarker} autoFocus={autoFocus} rows={rows} maxLength={maxLength} minLength={minLength} className={cx(textareaStyles, inputStyles, {
            [inputErrorStyles]: !!error,
            [inputDisabledStyles]: disabled,
            [inputReadOnlyStyles]: readOnly,
            [textareaAutoResizeStyles]: autoResize,
            [monospaceStyles]: monospace,
        })} onChange={handleChange} aria-invalid={!!error} aria-describedby={[description ? `${textareaId}-description` : null, error ? `${textareaId}-error` : null].filter(Boolean).join(" ") || undefined}/>
            </div>

            {error && <InputError error={error} id={`${textareaId}-error`}/>}
        </div>);
}
export default Textarea;
const containerStyles = css({
    display: "grid",
    gap: space[8],
    width: "100%",
    maxWidth: "600px",
});
const textareaContainerStyles = css({
    position: "relative",
    display: "grid",
});
const textareaStyles = css({
    boxSizing: "border-box",
    padding: space[8],
    border: `${borderWidth[1]} solid ${themeTokens.color.border.bold}`,
    borderRadius: borderRadius.small,
    backgroundColor: themeTokens.color.background.primary.default,
    color: themeTokens.color.text.primary,
    width: "100%",
    resize: "vertical",
    minHeight: "2.625rem", // Height of a single row of text at the defined font size
    "&:focus": {
        outline: `${borderWidth[2]} solid ${themeTokens.color.border.selected}`,
        outlineOffset: "0",
        borderColor: themeTokens.color.border.selected,
    },
    "&::placeholder": {
        color: themeTokens.color.text.tertiary,
    },
});
const monospaceStyles = css({
    font: text.code.regular.large,
});
const textareaAutoResizeStyles = css({
    resize: "none",
    overflow: "hidden",
});
